import { useChecklistMediaPreview } from '@/hooks/useChecklistMediaPreview';
import { isIOSChecklistMediaPreviewUri } from '@/utils/file/iosChecklistMediaSource';
import { subscribeResumedChecklistMedia } from '@/services/checklist/ChecklistImageService';
import { showChecklistMediaFeedback } from '@/services/checklist/checklistMediaFeedback';
import { mediaErrorCode } from '@/services/diagnostics/MediaDiagnosticHistory';
import { beginMediaAttempt, finishMediaAttempt } from '@/services/diagnostics/mediaAttemptRuntime';
import { captureSentryExceptionWithScope } from '@/services/monitoring/sentry';
import { createMediaDiagnosticRecorder } from '@/services/diagnostics/mediaDiagnosticRuntime';
import React, { useState, useEffect, useRef } from 'react';
import { View, Image, StyleSheet, TouchableOpacity, ActivityIndicator, Text, Platform, Alert } from 'react-native';
import * as FileSystem from 'expo-file-system/legacy';
import * as ImageManipulator from 'expo-image-manipulator';
import { Ionicons } from '@expo/vector-icons';
import theme from '../../config/theme';
import { useWebAlert, AlertButton } from '../WebAlert';
import { uploadChecklistPhoto, deleteChecklistPhoto } from '../../services/checklist/ChecklistImageService';
import { databaseService } from '../../services/databaseService';
import { APP_CONSTANTS } from '../../config/constants';
import { v4 as uuidv4 } from 'uuid';
import { ChecklistMarkFieldLogger } from '../../utils/logger';
import InlineMarkPad, { type InlineMarkPadHandle } from './InlineMarkPad';
import type { ChecklistMarkPoint } from '@/services/checklist/checklistMarking';
import { debugPause } from '@/utils/debugPauseUniversal';
import { traceImage } from '@/utils/debugImageTrace';

// Tipagem das props do campo de marcação do checklist
interface ChecklistMarkFieldProps {
    empresaId: string;
    checklistId: string;
    fieldId: string;
  backgroundFieldId?: string;
  value?: string;
  onChange: (uri: string) => void;
  points?: ChecklistMarkPoint[];
  onPointsChange?: (points: ChecklistMarkPoint[]) => void;
  onInteraction?: (kind: 'tap' | 'drag') => boolean;
  onCaptureStateChange?: (capturing: boolean) => void;
  editable?: boolean;
}

/**
 * Campo de marcação para checklist
 * Permite desenhar marcações sobre uma imagem de fundo, faz upload local-first e atualiza o draft
 */
const ChecklistMarkField: React.FC<ChecklistMarkFieldProps> = ({
    empresaId,
    checklistId,
    fieldId,
    backgroundFieldId = fieldId,
    value,
    onChange,
    points = [],
    onPointsChange = () => {},
    onInteraction = () => true,
    editable = true,
    onCaptureStateChange,
}) => {
    const [backgroundUri, setBackgroundUri] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const { showAlert } = useWebAlert();
    useEffect(() => subscribeResumedChecklistMedia(event => {
        if (event.checklistId === checklistId && event.fieldId === fieldId &&
            ((value ?? '') === event.expectedValue || value === event.localUri)) onChange(event.uri);
    }), [checklistId, fieldId, value, onChange]);
    const MARK_EXPORT_MAX_WIDTH = 1280;
    const MARK_EXPORT_QUALITY = 0.75;
    // WEB: quando value for id local de marcação, resolvemos para base64 do IndexedDB
    const resolvedUri = useChecklistMediaPreview(value, APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACOES.NAME, 'image/jpeg', { companyId: empresaId, checklistId, fieldId });
    const inlineMarkRef = useRef<InlineMarkPadHandle>(null);
    const saveInProgressRef = useRef(false);

    // Log de debug dos props recebidos
    /*useEffect(() => {
        ChecklistMarkFieldLogger.Main.info('[ChecklistMarkField] Props recebidos:', {
            empresaId,
            checklistId,
            fieldId,
            value,
        });
    }, [empresaId, checklistId, fieldId, value]);*/

    // Loga sempre que o value mudar
    /*useEffect(() => {
        ChecklistMarkFieldLogger.Main.info(`[ChecklistMarkField] value atualizado para campoId=${fieldId}:`, value);
    }, [value, fieldId]);*/

    // Função para mostrar alerta adaptável à plataforma
    const showMessage = (
        title: string,
        msg: string,
        buttons?: AlertButton[]
    ) => {
        if (Platform.OS !== 'android') {
            showAlert(title, msg, buttons ?? [{ text: 'OK' }]);
        } else {
            Alert.alert(title, msg, buttons ?? [{ text: 'OK' }]);
        }
    };

    // Carrega imagem de fundo vinda da tabela guincho_marcacao_imagens
    useEffect(() => {
        (async () => {
            try {
                //await debugPause('[ChecklistMarkField] Buscando imagem de fundo');
                //ChecklistMarkFieldLogger.Main.info(`[ChecklistMarkField] Buscando imagem de fundo para empresaId=${empresaId} campoId=${fieldId}`);
                const rows = await databaseService.listItems<any>(
                    APP_CONSTANTS.DATABASE.TABLES.GUINCHO_MARCACAO_IMAGENS.NAME,
                    { empresa_id: empresaId, campo_id: backgroundFieldId }
                );
                let imageUri: string | null = null;
                let foundLocal = false;
                if (rows && rows.length > 0) {
                    //await debugPause('[ChecklistMarkField] Imagem de fundo encontrada: ' + rows[0].foto_url);
                    // O cache histórico transacional também é legível offline no mobile.
                    try {
                        const localImage = await databaseService.readItem<any>(
                            APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACAO_BKG_IMAGES.NAME,
                            rows[0].id // usa o mesmo id
                        );
                        if (localImage?.base64 && localImage.empresa_id === empresaId
                            && localImage.template_id === rows[0].template_id && localImage.campo_id === backgroundFieldId) {
                            imageUri = `data:image/jpeg;base64,${localImage.base64}`;
                            foundLocal = true;
                            ChecklistMarkFieldLogger.Main.info(`[ChecklistMarkField] Imagem local carregada da tabela CHECKLIST_MARCACAO_BKG_IMAGES`);
                        }
                    } catch (localError) {
                        ChecklistMarkFieldLogger.Main.warn(`[ChecklistMarkField] Erro ao carregar imagem local: ${localError}`);
                    }
                    if (!foundLocal && Platform.OS !== 'web') {
                        // NOVO FLUXO MOBILE: busca exatamente pelo rowId
                        try {
                            const dirPath = `${FileSystem.documentDirectory}checklist-marcacao-bkg/${empresaId}/`;
                            const dirInfo = await FileSystem.getInfoAsync(dirPath);
                            if (dirInfo.exists) {
                                const files = await FileSystem.readDirectoryAsync(dirPath);
                                ChecklistMarkFieldLogger.Main.info(`[ChecklistMarkField] Arquivos disponíveis para background: ${files.length}`);

                                const rowId = rows[0].id;
                                const candidates = [`${rowId}.jpg`, `${rowId}.png`];
                                const match = files.find(f => candidates.includes(f));

                                if (match) {
                                    const fileUri = dirPath + match;
                                    imageUri = fileUri;
                                    foundLocal = true;
                                    ChecklistMarkFieldLogger.Main.info('[ChecklistMarkField] Imagem local encontrada');
                                } else {
                                    ChecklistMarkFieldLogger.Main.info(`[ChecklistMarkField] Nenhum arquivo compatível com rowId (${rowId}) encontrado.`);
                                }
                            } else {
                                ChecklistMarkFieldLogger.Main.info('[ChecklistMarkField] Diretório de backgrounds inexistente');
                            }
                        } catch (localError) {
                            ChecklistMarkFieldLogger.Main.warn(`[ChecklistMarkField] Erro ao buscar imagem local offline: ${localError}`);
                        }
                    }
                    // 2. Se não encontrou local, usa a URL remota
                    if (!foundLocal) {
                        imageUri = rows[0].foto_url;
                        ChecklistMarkFieldLogger.Main.info('[ChecklistMarkField] Usando imagem remota');
                    }
                } else {
                    ChecklistMarkFieldLogger.Main.info(`[ChecklistMarkField] Nenhuma imagem de fundo encontrada para campoId=${backgroundFieldId}`);
                }
                setBackgroundUri(imageUri);
            } catch (err) {
                ChecklistMarkFieldLogger.Main.error(`[ChecklistMarkField] Erro ao buscar imagem de marcação: ${err}`);
            }
        })();
    }, [backgroundFieldId, empresaId]);

    const drawingSession = useRef<string | null>(null);
    const onDrawingEvent = (phase: 'drawing_started' | 'drawing_cleared' | 'drawing_empty' | 'drawing_cancelled') => {
        const id = drawingSession.current ??= uuidv4();
        createMediaDiagnosticRecorder({ checklistId, fieldId, attemptId: id })(phase);
    };
    const saveAttempt = useRef<string | null>(null);
    const onSaveStart = async () => {
        const id = saveAttempt.current ?? drawingSession.current ?? uuidv4();
        saveAttempt.current = id;
        await beginMediaAttempt({ id, checklistId, fieldId, source: 'mark' });
        createMediaDiagnosticRecorder({ checklistId, fieldId, attemptId: id })('save_requested');
    };
    const onSaveError = (error: unknown) => {
        if (saveAttempt.current) finishMediaAttempt(saveAttempt.current, true, { phase: 'capture_failed', errorCode: mediaErrorCode(error) });
        saveAttempt.current = null;
    };
    const handleSaveMarks = async () => {
        if (!editable || saving || saveInProgressRef.current || points.length === 0 || !inlineMarkRef.current) return;
        saveInProgressRef.current = true;
        onCaptureStateChange?.(true);
        setSaving(true);
        try {
            await onSaveStart();
            const captured = await inlineMarkRef.current.capture();
            onCaptureStateChange?.(false);
            await handleSelectedImage(captured);
        } catch (error) {
            onSaveError(error);
            setSaving(false);
            showMessage('Não foi possível salvar', 'Suas marcações continuam nesta tela. Tente novamente quando a imagem estiver pronta.');
        } finally {
            saveInProgressRef.current = false;
            onCaptureStateChange?.(false);
        }
    };
    // Faz o upload da marcação e atualiza o draft
    const handleSelectedImage = async (dataUrl: string) => {
        const mediaAttemptId = saveAttempt.current ?? uuidv4();
        saveAttempt.current = null;
        drawingSession.current = null;
        await beginMediaAttempt({ id: mediaAttemptId, checklistId, fieldId, source: 'mark' });
        const recordMedia = createMediaDiagnosticRecorder({ checklistId, fieldId, attemptId: mediaAttemptId });
        recordMedia('capture_received');
        ChecklistMarkFieldLogger.Main.info('[handleSelectedImage] Iniciando seleção de marcação');
        setSaving(true);
        try {
            let localUri = dataUrl;
            
            // Processamento local da marcação (web e mobile)
            if (Platform.OS === 'web') {
                // Web: Ler a imagem como base64 e salvar na tabela CHECKLIST_PHOTOS
                const response = await fetch(dataUrl);
                const blob = await response.blob();
                const reader = new FileReader();
                const base64 = await new Promise<string>((resolve, reject) => {
                    reader.onerror = () => reject(reader.error);
                    reader.onload = () => resolve((reader.result as string).split(',')[1]);
                    reader.readAsDataURL(blob);
                });
                // Mantém extensão compatível com o mime do dataUrl
                const extension = dataUrl.startsWith('data:image/jpeg') ? 'jpg' : 'png';
                ChecklistMarkFieldLogger.Main.info(`[handleSelectedImage] Extensão detectada: ${extension}`);
                // Gerar id único para a imagem
                const destPath = `checklist-marcacoes/${empresaId}/${checklistId}-${fieldId}-${Date.now()}-${mediaAttemptId}.${extension}`;
                ChecklistMarkFieldLogger.Main.info('[handleSelectedImage] Salvando marcação no IndexedDB');
                // Salvar na tabela CHECKLIST_PHOTOS
                await databaseService.createItem(
                    APP_CONSTANTS.DATABASE.TABLES.CHECKLIST_MARCACOES.NAME,
                    {
                        id: destPath,
                        base64,
                        created_at: Date.now(),
                        updated_at: Date.now(),
                    },
                    true // skipPendingOperation
                );
                // Deletar a URI temporária (blob:...)
                try {
                    ChecklistMarkFieldLogger.Main.info('[handleSelectedImage] Deletando URI temporária');
                    URL.revokeObjectURL(dataUrl);
                } catch { }
                localUri = destPath;
            } else {
                // Mobile: Extrair base64 do dataURL e salvar como arquivo físico
                const base64Data = dataUrl.split(',')[1]; // Remove o prefixo data:image/<formato>;base64,
                const captureMime = /^data:image\/(jpeg|jpg|png);base64,/i.exec(dataUrl)?.[1]?.toLowerCase();
                if (!captureMime) throw new Error('Formato da captura de marcação inválido');
                const captureExtension = captureMime === 'png' ? 'png' : 'jpg';
                
                // Gerar caminho único para o arquivo
                const timestamp = Date.now();
                const baseName = `${checklistId}-${fieldId}-${timestamp}-${mediaAttemptId}`;
                const tempFileName = `${baseName}-capture.${captureExtension}`;
                const finalFileName = `${baseName}.jpg`;
                const tempDestPath = `checklist-marcacoes/${empresaId}/${tempFileName}`;
                const finalDestPath = `checklist-marcacoes/${empresaId}/${finalFileName}`;
                
                ChecklistMarkFieldLogger.Main.info('[handleSelectedImage] Mobile: Salvando marcação como arquivo');
                
                // Salvar o arquivo no sistema de arquivos usando FileSystem
                const fileUri = `${FileSystem.documentDirectory}${tempDestPath}`;
                
                // Criar diretório se necessário
                const dirPath = `${FileSystem.documentDirectory}checklist-marcacoes/${empresaId}/`;
                const dirInfo = await FileSystem.getInfoAsync(dirPath);
                if (!dirInfo.exists) {
                    await FileSystem.makeDirectoryAsync(dirPath, { intermediates: true });
                }
                
                // Escrever o arquivo
                await FileSystem.writeAsStringAsync(fileUri, base64Data, {
                    encoding: FileSystem.EncodingType.Base64
                });
                
                ChecklistMarkFieldLogger.Main.info('[handleSelectedImage] Mobile: Arquivo salvo localmente');
                localUri = fileUri;

                // Comprimir e converter para JPEG mantendo proporção
                try {
                    const imageSize = await new Promise<{ width: number; height: number }>((resolve, reject) => {
                        Image.getSize(
                            fileUri,
                            (width, height) => resolve({ width, height }),
                            (error) => reject(error)
                        );
                    });
                    const actions: ImageManipulator.Action[] = [];
                    if (imageSize.width > MARK_EXPORT_MAX_WIDTH) {
                        actions.push({ resize: { width: MARK_EXPORT_MAX_WIDTH } });
                    }
                    const manipulated = await (ImageManipulator as any).manipulateAsync(
                        fileUri,
                        actions,
                        { compress: MARK_EXPORT_QUALITY, format: ImageManipulator.SaveFormat.JPEG }
                    );
                    const finalFileUri = `${FileSystem.documentDirectory}${finalDestPath}`;
                    await FileSystem.moveAsync({ from: manipulated.uri, to: finalFileUri });
                    await FileSystem.deleteAsync(fileUri, { idempotent: true });
                    localUri = finalFileUri;
                    ChecklistMarkFieldLogger.Main.info('[handleSelectedImage] Mobile: Compressão concluída e arquivo final salvo');
                } catch (compressErr) {
                    ChecklistMarkFieldLogger.Main.warn(`[handleSelectedImage] Mobile: Falha ao comprimir marcação: ${compressErr}`);
                }
            }
            
            ChecklistMarkFieldLogger.Main.info('[handleSelectedImage] Chamando uploadChecklistPhoto');
            
            recordMedia('local_file_ready');
            const result = await uploadChecklistPhoto(checklistId, fieldId, localUri, onChange, mediaAttemptId, value ?? '');
            if (result.superseded) return;
            if (result.success && result.data?.uri) {
                ChecklistMarkFieldLogger.Main.info(`[handleSelectedImage] ${result.remoteAcknowledged === true ? 'Mídia enviada; referência remota confirmada.' : 'Mídia salva localmente; envio pendente.'}`);
                onChange(result.data.uri); // Atualiza o draft com a nova URI/URL
                onPointsChange([]);
                showChecklistMediaFeedback(result, 'Marcação', showMessage, onChange);
            } else {
                ChecklistMarkFieldLogger.Main.info(`[handleSelectedImage] Upload falhou ou parcial. Erro: ${result.error}`);
                showChecklistMediaFeedback(result, 'Marcação', showMessage, onChange);
            }
        } catch (err) {
            recordMedia('local_file_failed', err);
            captureSentryExceptionWithScope(err, {
                context: { flow: 'checklist_media', stage: 'local_file', checklistId, campoId: fieldId, attemptId: mediaAttemptId },
                tags: { flow: 'upload', stage: 'local_file' },
            });
            ChecklistMarkFieldLogger.Main.info(`[handleSelectedImage] Exceção lançada: ${err}`);
            showChecklistMediaFeedback({ success: false }, 'Marcação', showMessage, onChange);
        } finally {
            setSaving(false);
            ChecklistMarkFieldLogger.Main.info('[handleSelectedImage] Finalizado');
        }
    };

    // Função para confirmar e remover a marcação
    const handleRemoveMarkField = () => {
        const confirmRemove = () => {
            showMessage(
                'Confirmar remoção',
                'Tem certeza que deseja remover esta marcação?',
                [
                    { text: 'Cancelar', style: 'cancel' },
                    { text: 'Remover', style: 'destructive', onPress: performRemove }
                ]
            );
        };

        const performRemove = async () => {
            ChecklistMarkFieldLogger.Main.info(`[handleRemoveMarkField] Iniciando remoção para fieldId=${fieldId}`);
            setDeleting(true);
            try {
                const result = await deleteChecklistPhoto(checklistId, fieldId);
                if (result.success) {
                    ChecklistMarkFieldLogger.Main.info(`[handleRemoveMarkField] Remoção concluída com sucesso`);
                    onChange(''); // Limpa o valor no draft
                    showMessage('Sucesso', 'Marcação removida com sucesso!');
                } else {
                    ChecklistMarkFieldLogger.Main.info(`[handleRemoveMarkField] Remoção falhou: ${result.error}`);
                    showMessage('Erro', result.error || 'Falha ao remover a marcação.');
                }
            } catch (err) {
                ChecklistMarkFieldLogger.Main.info(`[handleRemoveMarkField] Exceção lançada: ${err}`);
                showMessage('Erro', 'Falha ao remover a marcação.');
            } finally {
                setDeleting(false);
                ChecklistMarkFieldLogger.Main.info(`[handleRemoveMarkField] Finalizado para fieldId=${fieldId}`);
            }
        };

        confirmRemove();
    };

    // Um valor histórico sem preview resolvido nunca deve ser substituído pelo fundo em branco.
    const resolvingIOSFile = Platform.OS === 'ios' && isIOSChecklistMediaPreviewUri(value);
    const savedImageUri = resolvedUri || (!resolvingIOSFile && /^(https?:|file:|content:|data:)/i.test(value ?? '') ? value ?? null : null);
    const imageUri = savedImageUri || (value ? null : backgroundUri);
    const uriToShow = imageUri || backgroundUri;
    const shouldShowImageSource = APP_CONSTANTS.FEATURE_FLAGS?.SHOW_DEBUG_ACTIONS === true &&
        APP_CONSTANTS.DEBUG?.SHOW_IMAGE_SOURCE === true;
    useEffect(() => {
        if (uriToShow) {
            traceImage('component.mark.renderSource', { checklistId, campoId: fieldId, src: uriToShow });
        }
    }, [checklistId, fieldId, uriToShow]);

    const handleUndo = () => {
        if (!editable || saving || points.length === 0) return;
        const remaining = points.slice(0, -1);
        if (remaining.length === 0) onDrawingEvent('drawing_cleared');
        onPointsChange(remaining);
    };

    // Renderização do campo de marcação
    return (
        <View style={styles.container}>
            {imageUri ? <InlineMarkPad
                ref={inlineMarkRef}
                fieldId={fieldId}
                sourceUri={imageUri}
                previewScopeKey={`${empresaId}:${checklistId}:${fieldId}`}
                points={points}
                disabled={!editable || saving || deleting}
                onPointsChange={onPointsChange}
                onInteraction={onInteraction}
                onDrawingEvent={onDrawingEvent}
            /> : <View style={styles.unavailable}>
                <Ionicons name="image-outline" size={24} color={theme.colors.text.tertiary} />
                <Text style={styles.unavailableText}>{value ? 'Carregando imagem salva…' : 'Imagem de marcação indisponível'}</Text>
            </View>}
            {shouldShowImageSource && uriToShow && (
              <Text style={{ fontSize: 11, color: '#666', marginTop: 4 }} numberOfLines={2}>
                {`src: ${uriToShow.slice(0, 120)}`}
              </Text>
            )}
            <Text style={styles.instructions}>{!editable && points.length > 0
                ? 'Você pode descartar os pontos pendentes. A imagem e as respostas salvas serão preservadas.'
                : 'Toque uma vez em cada ponto da imagem para adicionar círculos.'}</Text>
            <View style={styles.actionButtons}>
                {!editable && points.length > 0 ? <TouchableOpacity
                    testID={`mark-discard-${fieldId}`} accessibilityRole="button"
                    style={styles.actionButton} disabled={saving || deleting}
                    onPress={() => {
                        if (editable || saving || deleting) return;
                        onPointsChange([]);
                        onDrawingEvent('drawing_cleared');
                    }}>
                    <Text style={styles.actionButtonText}>Descartar marcações pendentes</Text>
                </TouchableOpacity> : null}
                <TouchableOpacity testID={`mark-undo-${fieldId}`} accessibilityRole="button" style={styles.actionButton} onPress={handleUndo} disabled={!editable || saving || points.length === 0}>
                    <Text style={[styles.actionButtonText, points.length === 0 && styles.disabledText]}>Desfazer último</Text>
                </TouchableOpacity>
                <TouchableOpacity testID={`mark-save-${fieldId}`} accessibilityRole="button" style={[styles.actionButton, styles.saveButton]} onPress={handleSaveMarks} disabled={!editable || saving || points.length === 0}>
                    {saving ? <ActivityIndicator size="small" color="#fff" /> : <Text style={[styles.actionButtonText, styles.saveText]}>Salvar marcação</Text>}
                </TouchableOpacity>
                {value ? <TouchableOpacity style={[styles.actionButton, styles.removeButton]} onPress={handleRemoveMarkField} disabled={!editable || deleting || saving || points.length > 0}>
                    {deleting ? <ActivityIndicator size="small" color={theme.colors.util.error} /> : <Text style={[styles.actionButtonText, { color: theme.colors.util.error }]}>Remover imagem</Text>}
                </TouchableOpacity> : null}
            </View>
        </View>
    );
}

// Estilos seguindo o padrão do theme
const styles = StyleSheet.create({
    container: {
        width: '100%',
    },
    unavailable: {
        width: '100%',
        minHeight: 180,
        borderRadius: theme.borderRadius.sm,
        backgroundColor: theme.colors.background.secondary,
        justifyContent: 'center',
        alignItems: 'center',
        borderWidth: 1,
        borderColor: theme.colors.border.light,
        gap: 8,
    },
    unavailableText: { color: theme.colors.text.secondary, fontSize: 13 },
    instructions: { fontSize: 12, color: theme.colors.text.secondary, marginTop: theme.spacing.xs },
    actionButtons: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        marginTop: theme.spacing.sm,
        gap: theme.spacing.sm,
    },
    actionButton: {
        minHeight: 44,
        justifyContent: 'center',
        borderWidth: 1,
        borderColor: theme.colors.primary.default,
        borderRadius: theme.borderRadius.sm,
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
    },
    actionButtonText: {
        fontSize: theme.fontSizes.xs,
        fontWeight: theme.fontWeights.medium as any,
    },
    removeButton: {
        borderColor: theme.colors.util.error,
    },
    saveButton: { backgroundColor: theme.colors.primary.default },
    saveText: { color: '#fff' },
    disabledText: { color: theme.colors.text.tertiary },
});

export default ChecklistMarkField; 
